import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
    RipeningCycleStartResult,
    useEndRipeningCycle,
    useRefreshRipeningCycleStart,
    useRetryRipeningCycleStart,
} from "../../../../hooks/useFacilityLocationQueries";
import { useRipeningCommand } from "../../../../hooks/useRipeningPrograms";
import { toast } from "../../../../utils/toast";
import {
    CYCLE_START_STALE_MS,
    CycleStartStatus,
    getCycleStartCommand,
    isFailedStartStatus,
    parseCycleStartTimestamp,
} from "../RipeningRoom/ripeningProgramCycle";

export const CYCLE_START_POLL_MS = 2500;
// * Stop waiting for a start that is still unanswered this long after it was sent.
export const CYCLE_START_GIVE_UP_MS = 7 * 60 * 1000;
export const CYCLE_START_SUCCESS_MESSAGE = "✅ The ripening room has started.";
export const CYCLE_START_CANCELLED_MESSAGE = "The start was cancelled and the cycle has been ended.";
// * Only used when the stored message cannot be read; the backend owns the real messages.
export const CYCLE_START_FALLBACK_MESSAGES: Record<"rejected" | "timeout", string> = {
    rejected: "The ripening room could not start the program. Try again or contact support.",
    timeout: "The ripening room did not respond. Check that it is online and try again.",
};

export interface CycleStart {
    cycleId: string;
    commandId: string;
    status: CycleStartStatus;
    message?: string | null;
    // * When the start was sent (ms), for the elapsed time while waiting.
    startedAt: number;
    // * When the room confirmed the start (ms), set only in this session.
    acceptedAt?: number;
    // * Started (or retried) by the user in this session: tell them the outcome.
    watched: boolean;
}

export interface CycleStartTracker {
    cycleStart: CycleStart | null;
    cycleStartFeedbackOpen: boolean;
    openCycleStartFeedback: () => void;
    closeCycleStartFeedback: () => void;
    trackCycleStart: (result: RipeningCycleStartResult | undefined) => void;
    retryCycleStart: () => Promise<void>;
    retryCycleStartLoading: boolean;
    cancelCycleStart: () => Promise<void>;
    cancelCycleStartLoading: boolean;
}

export const emptyCycleStartTracker: CycleStartTracker = {
    cycleStart: null,
    cycleStartFeedbackOpen: false,
    openCycleStartFeedback: () => null,
    closeCycleStartFeedback: () => null,
    trackCycleStart: () => null,
    retryCycleStart: async () => undefined,
    retryCycleStartLoading: false,
    cancelCycleStart: async () => undefined,
    cancelCycleStartLoading: false,
};

function failureMessage(status: CycleStartStatus, message?: string | null) {
    if (message) return message;
    return isFailedStartStatus(status) ? CYCLE_START_FALLBACK_MESSAGES[status] : null;
}

function FailedStartToast({ message, onTryAgain }: { message: string; onTryAgain: () => void }) {
    return (
        <div>
            <div>{message}</div>
            <button type="button" className="btn btn-sm btn-light mt-2" onClick={onTryAgain}>
                Try again
            </button>
        </div>
    );
}

/**
 * Follows the program start of the room's active cycle until the room confirmed it
 * (accepted) or it failed (rejected / timeout), and offers "Try again" / "Cancel start".
 *
 * Polls the start command every 2.5 s while pending and stores the final status on the
 * cycle (backend refresh). Keeps following when the start dialog is closed, and then tells
 * the user the outcome with one toast.
 */
export function useCycleStartTracker({
    locationId,
    location,
    activeCycle,
    refreshRipeningCycles,
}: {
    locationId: string | undefined;
    location: any;
    activeCycle: any;
    refreshRipeningCycles: () => void;
}): CycleStartTracker {
    const queryClient = useQueryClient();
    const [cycleStart, setCycleStart] = useState<CycleStart | null>(null);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const feedbackOpenRef = useRef(false);
    feedbackOpenRef.current = feedbackOpen;
    const resolvingCommandRef = useRef<string | null>(null);

    const refreshMutation = useRefreshRipeningCycleStart();
    const retryMutation = useRetryRipeningCycleStart();
    const endMutation = useEndRipeningCycle();

    const openCycleStartFeedback = useCallback(() => setFeedbackOpen(true), []);
    const closeCycleStartFeedback = useCallback(() => setFeedbackOpen(false), []);

    const refreshRoomRun = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ["ripeningRoomRun"] });
    }, [queryClient]);

    // * Pick up a start that is still open when the page loads (or the cycle list changes).
    const storedCommand = getCycleStartCommand(activeCycle);
    useEffect(() => {
        if (!activeCycle?.id) return;
        setCycleStart((previous) => {
            if (previous?.cycleId === activeCycle.id) return previous;
            if (!storedCommand?.command_id || !storedCommand.status || storedCommand.status === "accepted") {
                return previous?.watched ? previous : null;
            }
            return {
                cycleId: activeCycle.id,
                commandId: storedCommand.command_id,
                status: storedCommand.status,
                message: storedCommand.message,
                startedAt: parseCycleStartTimestamp(activeCycle.ripening_start_date),
                watched: false,
            };
        });
    }, [activeCycle?.id, storedCommand?.command_id, storedCommand?.status]);

    const applyFinalStatus = useCallback((start: CycleStart, status: CycleStartStatus, message?: string | null) => {
        const next: CycleStart = {
            ...start,
            status,
            message: failureMessage(status, message),
            acceptedAt: status === "accepted" ? Date.now() : undefined,
        };
        setCycleStart(next);
        refreshRipeningCycles();
        refreshRoomRun();
        if (status === "accepted") {
            setFeedbackOpen(false);
            if (start.watched) toast.success(CYCLE_START_SUCCESS_MESSAGE);
        } else if (start.watched && !feedbackOpenRef.current && next.message) {
            toast.error(<FailedStartToast message={next.message} onTryAgain={() => setFeedbackOpen(true)} />, {
                autoClose: false,
            });
        }
    }, [refreshRipeningCycles, refreshRoomRun]);

    const isPending = cycleStart?.status === "pending";
    const isStale = !!cycleStart && !cycleStart.watched && Date.now() - cycleStart.startedAt > CYCLE_START_STALE_MS;
    const pollCommandId = isPending && !isStale ? cycleStart.commandId : undefined;
    const commandQuery = useRipeningCommand(
        location?.gateway,
        pollCommandId,
        { tenantId: location?.tenant_id },
        { intervalMs: CYCLE_START_POLL_MS, silent: true },
    );
    const observedStatus = commandQuery.data?.command_id === pollCommandId || !commandQuery.data?.command_id
        ? commandQuery.data?.status
        : undefined;

    // * The room answered: store the outcome on the cycle and use the stored message.
    useEffect(() => {
        if (!cycleStart || !pollCommandId || !observedStatus || observedStatus === "pending") return;
        if (resolvingCommandRef.current === pollCommandId || !locationId) return;
        resolvingCommandRef.current = pollCommandId;
        const start = cycleStart;
        refreshMutation.mutateAsync({ locationId, cycleId: start.cycleId })
            .then((result) => {
                const status = result?.command_status && result.command_status !== "pending"
                    ? result.command_status
                    : observedStatus;
                applyFinalStatus(start, status, result?.command_message);
            })
            .catch(() => applyFinalStatus(start, observedStatus, null));
    }, [observedStatus, pollCommandId, locationId]);

    // * Ask the backend once for the outcome, without polling: for an old start picked up
    // * from the cycle, and for a start the room has not answered for too long.
    const resolveOnce = useCallback((start: CycleStart, giveUp: boolean) => {
        if (!locationId || resolvingCommandRef.current === start.commandId) return;
        resolvingCommandRef.current = start.commandId;
        const unresolved = () => {
            resolvingCommandRef.current = null;
            setCycleStart((previous) => (previous?.commandId === start.commandId ? null : previous));
            if (giveUp) {
                setFeedbackOpen(false);
                refreshRipeningCycles();
            }
        };
        refreshMutation.mutateAsync({ locationId, cycleId: start.cycleId })
            .then((result) => {
                const status = result?.command_status;
                if (status && status !== "pending") applyFinalStatus(start, status, result?.command_message);
                else unresolved();
            })
            .catch(unresolved);
    }, [locationId, applyFinalStatus, refreshRipeningCycles]);

    useEffect(() => {
        if (isPending && isStale && cycleStart) resolveOnce(cycleStart, false);
    }, [isPending, isStale, cycleStart?.commandId]);

    useEffect(() => {
        if (!pollCommandId || !cycleStart) return undefined;
        const start = cycleStart;
        const timer = setTimeout(
            () => resolveOnce(start, true),
            Math.max(0, start.startedAt + CYCLE_START_GIVE_UP_MS - Date.now()),
        );
        return () => clearTimeout(timer);
    }, [pollCommandId]);

    const trackCycleStart = useCallback((result: RipeningCycleStartResult | undefined) => {
        if (!result?.command_id || !result.cycle_id) return;
        const start: CycleStart = {
            cycleId: result.cycle_id,
            commandId: result.command_id,
            status: result.command_status || "pending",
            message: result.command_message,
            startedAt: Date.now(),
            watched: true,
        };
        refreshRoomRun();
        if (start.status === "pending") {
            setCycleStart(start);
            setFeedbackOpen(true);
            return;
        }
        feedbackOpenRef.current = start.status !== "accepted";
        setFeedbackOpen(feedbackOpenRef.current);
        applyFinalStatus({ ...start, status: "pending" }, start.status, result.command_message);
    }, [applyFinalStatus, refreshRoomRun]);

    const retryCycleStart = useCallback(async () => {
        if (!locationId || !cycleStart) return;
        try {
            const result = await retryMutation.mutateAsync({ locationId, cycleId: cycleStart.cycleId });
            refreshRipeningCycles();
            if (result?.warning) toast.warning(result.warning, { autoClose: false });
            trackCycleStart({ ...result, cycle_id: result?.cycle_id || cycleStart.cycleId });
        } catch {
            // * The error is shown by the shared API client; the failed state stays for another try.
        }
    }, [locationId, cycleStart, retryMutation, refreshRipeningCycles, trackCycleStart]);

    const cancelCycleStart = useCallback(async () => {
        if (!locationId || !cycleStart) return;
        try {
            // * The room refused (or never confirmed) this start: leave whatever it runs alone.
            await endMutation.mutateAsync({ locationId, cycleId: cycleStart.cycleId, payload: { stop_room: false } });
            setCycleStart(null);
            setFeedbackOpen(false);
            refreshRipeningCycles();
            toast.info(CYCLE_START_CANCELLED_MESSAGE);
        } catch {
            // * The error is shown by the shared API client.
        }
    }, [locationId, cycleStart, endMutation, refreshRipeningCycles]);

    return {
        cycleStart,
        cycleStartFeedbackOpen: feedbackOpen && !!cycleStart,
        openCycleStartFeedback,
        closeCycleStartFeedback,
        trackCycleStart,
        retryCycleStart,
        retryCycleStartLoading: retryMutation.isPending,
        cancelCycleStart,
        cancelCycleStartLoading: endMutation.isPending,
    };
}
